import { cx } from "@emotion/css";
import Drawer from "@mui/material/Drawer";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SwipeableContent } from "src/components/Default/LekUI/SwipableDrawer";
import { ChatbotComposer } from "src/components/GlobalChatbot/ChatbotComposer";
import { useChatbotContext } from "src/components/GlobalChatbot/ChatbotContext";
import { ChatbotConversationDetails } from "src/components/GlobalChatbot/ChatbotConversationDetails";
import { ChatbotHeader } from "src/components/GlobalChatbot/ChatbotHeader";
import { ChatbotHistoryScreen } from "src/components/GlobalChatbot/ChatbotHistoryScreen";
import { ChatbotHistorySidebar } from "src/components/GlobalChatbot/ChatbotHistorySidebar";
import { ChatbotMessageList } from "src/components/GlobalChatbot/ChatbotMessageList";
import { ShortLimitedAccess } from "src/components/NewComponents/LimitedAccess";
import { hasLimitedAccess } from "src/helpers/getPermissions";
import isMobile, { isTablet } from "src/helpers/isMobile";
import { useChatbotLimits } from "src/hooks/useChatbotLimits";
import { useGlobalChatbotConversation } from "src/hooks/useGlobalChatbotConversation";

interface ChatbotPanelProps {
	isFullWidth: boolean;
	onToggleFullWidth: (isFullWidth: boolean) => void;
	activeConversationId: string | null;
	onActiveConversationChange: (conversationId: string | null) => void;
	onClose: () => void;
}

type ChatbotView = "chat" | "history";

export const ChatbotPanel = ({
	isFullWidth: isFullWidthRequested,
	onToggleFullWidth,
	activeConversationId,
	onActiveConversationChange: setActiveConversationId,
	onClose,
}: ChatbotPanelProps) => {
	const { t } = useTranslation();
	const [view, setView] = useState<ChatbotView>("chat");
	const { attachedQuestion } = useChatbotContext();

	const limitedAccess = hasLimitedAccess();

	// Na mobile (dolny) i tablecie (boczny) panel jest drawerem — full screen nie istnieje
	// (także gdy URL ma chatbotFull=1). Na tablecie docked panel ściskałby pytanie.
	const mobile = isMobile();
	const tablet = !mobile && isTablet();
	const isFullWidth = isFullWidthRequested && !mobile && !tablet;

	const { data: limits } = useChatbotLimits(!limitedAccess);

	const {
		messages,
		details,
		sendMessage,
		isStreaming,
		isSubmitted,
		isWaitingForFirstToken,
		isLoadingMessages,
	} = useGlobalChatbotConversation({
		conversationId: activeConversationId,
		onConversationCreated: setActiveConversationId,
	});

	const handleSend = (text: string) => sendMessage(text, attachedQuestion);

	// Status przełącza się na "streaming" już przy text-start, zanim dotrze
	// pierwszy token — dopóki treść jest pusta, pokazujemy loader "myślę...".
	const showThinking = isSubmitted || isWaitingForFirstToken;
	const visibleMessages = isWaitingForFirstToken
		? messages.slice(0, -1)
		: messages;

	const openConversation = (conversationId: string | null) => {
		setActiveConversationId(conversationId);
		setView("chat");
	};

	const handleConversationDeleted = (conversationId: string) => {
		if (conversationId === activeConversationId) {
			setActiveConversationId(null);
		}
	};

	const showHistoryScreen = view === "history" && !isFullWidth;

	const column = (
		<div className="chatbot-panel-column">
			{showHistoryScreen ? (
				<ChatbotHistoryScreen
					activeConversationId={activeConversationId}
					onSelect={openConversation}
					onDeleted={handleConversationDeleted}
					onBack={() => setView("chat")}
					onNewConversation={() => openConversation(null)}
					onClose={onClose}
				/>
			) : (
				<>
					<ChatbotHeader
						isFullWidth={isFullWidth}
						onToggleFullWidth={
							mobile || tablet
								? undefined
								: () => onToggleFullWidth(!isFullWidth)
						}
						activeConversationId={activeConversationId}
						onSelectConversation={openConversation}
						onNewConversation={() => openConversation(null)}
						onOpenHistory={() => setView("history")}
						onClose={onClose}
					/>
					{limitedAccess ? (
						<ShortLimitedAccess />
					) : (
						<>
							{!!details?.questions.length && (
								<ChatbotConversationDetails
									key={activeConversationId}
									details={details}
								/>
							)}
							<ChatbotMessageList
								messages={visibleMessages}
								showThinking={showThinking}
								isLoading={isLoadingMessages}
							/>
							<ChatbotComposer
								onSend={handleSend}
								isLoading={isStreaming || isSubmitted}
								limits={limits}
							/>
						</>
					)}
				</>
			)}
		</div>
	);

	if (mobile) {
		return (
			<SwipeableContent isOpen onClose={onClose}>
				<div className="chatbot-panel chatbot-panel--drawer">{column}</div>
			</SwipeableContent>
		);
	}

	if (tablet) {
		return (
			<Drawer
				anchor="right"
				open
				onClose={onClose}
				classes={{ paper: "chatbot-side-drawer" }}
			>
				<div className="chatbot-panel chatbot-panel--drawer">{column}</div>
			</Drawer>
		);
	}

	return (
		// Bez full width panel dokuje się w flexie strony (jak lista pytań),
		// full width = overlay. Jedno drzewo, żeby przełączanie nie remountowało czatu.
		<div
			className={cx("chatbot-panel-overlay", {
				"chatbot-panel-overlay--docked": !isFullWidth,
			})}
		>
			<button
				type="button"
				className={cx("chatbot-panel-backdrop", {
					"chatbot-panel-backdrop--visible": isFullWidth,
				})}
				onClick={() => onToggleFullWidth(false)}
				aria-label={t("globalChatbot.collapse", "Zwiń do panelu")}
			/>

			<div
				className={cx("chatbot-panel", { "chatbot-panel--full": isFullWidth })}
			>
				{isFullWidth && (
					<ChatbotHistorySidebar
						activeConversationId={activeConversationId}
						onSelect={openConversation}
						onDeleted={handleConversationDeleted}
						onNewConversation={() => openConversation(null)}
					/>
				)}

				{column}
			</div>
		</div>
	);
};
